import {
    ArrowLeft,
    History,
    Search,
    X,
} from "lucide-react";
import {
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
    clearSuggestions,
    getSuggestions,
} from "../../../Redux/Search_apis/slice";
import {
    getRecentSearches,
    recordActivity,
} from "../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../Redux/ActivityApi/types";
import "./SearchPage.css";

const SearchPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const inputRef = useRef<HTMLInputElement>(null);

    const initialQuery = searchParams.get("q") ?? "";

    const [searchValue, setSearchValue] = useState(initialQuery);
    const [hiddenSearches, setHiddenSearches] = useState<string[]>([]);

    const suggestions = useAppSelector(
        (state) => state.search.suggestions.data
    );
    const recentSearches = useAppSelector(
        (state) => state.activity.recentSearches
    );

    useEffect(() => {
        dispatch(getRecentSearches());
    }, [dispatch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchValue.trim().length > 1) {
                dispatch(getSuggestions(searchValue.trim()));
            } else {
                dispatch(clearSuggestions());
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchValue, dispatch]);

    const visibleSearches = useMemo(() => {
        const term = searchValue.trim();

        if (term) {
            return suggestions
                .map((suggestion) => suggestion.text)
                .filter((text) => !hiddenSearches.includes(text));
        }

        return (recentSearches || []).filter(
            (text) => !hiddenSearches.includes(text)
        );
    }, [searchValue, suggestions, recentSearches, hiddenSearches]);

    const listTitle = searchValue.trim()
        ? "Related searches"
        : "Recent searches";

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 860px)");

        const protectDesktop = () => {
            if (!mediaQuery.matches) {
                navigate("/HomePage", { replace: true });
            }
        };

        protectDesktop();
        mediaQuery.addEventListener("change", protectDesktop);

        return () =>
            mediaQuery.removeEventListener("change", protectDesktop);
    }, [navigate]);

    useEffect(() => {
        const timer = window.setTimeout(
            () => inputRef.current?.focus(),
            40
        );
        return () => window.clearTimeout(timer);
    }, []);

    const submitSearch = (value: string) => {
        if (!value) {
            inputRef.current?.focus();
            return;
        }

        dispatch(
            recordActivity({
                activities: {
                    eventType: ActivityType.SEARCH,
                    searchQuery: value,
                },
            })
        );

        navigate(
            `/Categories/All?${new URLSearchParams({ q: value }).toString()}`
        );
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const value = searchValue.trim();
        if (!value) {
            inputRef.current?.focus();
            return;
        }

        setSearchParams({ q: value }, { replace: true });
        inputRef.current?.blur();
        submitSearch(value);
    };

    const handleSelect = (term: string) => {
        setSearchValue(term);
        setSearchParams({ q: term }, { replace: true });
        submitSearch(term);
    };

    const handleDelete = (term: string) => {
        setHiddenSearches((prev) =>
            prev.includes(term) ? prev : [...prev, term]
        );
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate("/HomePage", { replace: true });
    };

    return (
        <main className="salar-search-page">
            <header className="salar-search-page-header">
                <div className="salar-search-page-header-inner">
                    <button
                        type="button"
                        className="salar-search-page-back"
                        onClick={handleBack}
                        aria-label="Go back"
                    >
                        <ArrowLeft size={23} strokeWidth={2} />
                    </button>

                    <form
                        className="salar-search-page-form"
                        onSubmit={handleSubmit}
                    >
                        <Search size={21} strokeWidth={2} />

                        <input
                            ref={inputRef}
                            type="search"
                            value={searchValue}
                            onChange={(event) =>
                                setSearchValue(event.target.value)
                            }
                            placeholder="Search products, brands and categories"
                            aria-label="Search Salar"
                            autoComplete="off"
                            inputMode="search"
                        />

                        {searchValue && (
                            <button
                                type="button"
                                className="salar-search-page-clear"
                                onClick={() => {
                                    setSearchValue("");
                                    setSearchParams({}, { replace: true });
                                    window.setTimeout(
                                        () => inputRef.current?.focus(),
                                        0
                                    );
                                }}
                                aria-label="Clear search"
                            >
                                <X size={18} strokeWidth={2} />
                            </button>
                        )}

                        <button
                            type="submit"
                            className="salar-search-page-submit"
                        >
                            Search
                        </button>
                    </form>
                </div>
            </header>

            <div className="salar-search-page-content">
                <section className="salar-search-suggestions-card">
                    <div className="salar-search-suggestions-head">
                        <div>
                            <h2>{listTitle}</h2>
                            <p>
                                {searchValue.trim()
                                    ? "Suggestions update as you type."
                                    : "Tap a search to use it again."}
                            </p>
                        </div>

                        <span>{visibleSearches.length}</span>
                    </div>

                    <div className="salar-search-suggestions-list">
                        {visibleSearches.length > 0 ? (
                            visibleSearches.map((term) => (
                                <div
                                    className="salar-search-suggestion-row"
                                    key={term}
                                >
                                    <button
                                        type="button"
                                        className="salar-search-suggestion-main"
                                        onClick={() => handleSelect(term)}
                                    >
                                        <span className="salar-search-suggestion-icon">
                                            <History size={18} strokeWidth={1.9} />
                                        </span>
                                        <span className="salar-search-suggestion-copy">
                                            {term}
                                        </span>
                                    </button>

                                    <button
                                        type="button"
                                        className="salar-search-suggestion-remove"
                                        onClick={() => handleDelete(term)}
                                        aria-label={`Remove ${term} from recent searches`}
                                    >
                                        <X size={18} strokeWidth={2} />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="salar-search-empty">
                                <span>
                                    <Search size={23} strokeWidth={1.8} />
                                </span>
                                <strong>No matching searches</strong>
                                <p>
                                    Try a different product, brand or category
                                    name.
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </main>
    );
};

export default SearchPage;
